import React from "react";
import * as Icon from "react-feather";

import JaneReportService from "../../../../services/JaneReportService";
import UtilityService from "../../../../services/UtilityService";

import WComponent from "../../../common/WComponent";
import Loader from "../../../common/loader/Loader";

import ToolsCollapsiblePanel from "../kit/ToolsCollapsiblePanel";
import ToolsMappingRow from "../kit/ToolsMappingRow";
import ToolsNotice from "../kit/ToolsNotice";
import ToolsProgressBadge from "../kit/ToolsProgressBadge";

import { describeCorrespondenceSource } from "./correspondenceSource";

import "./correspondence-panel.css";
import "./item-heads-panel.css";

class ItemHeadsPanel extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			isSaving: false,
			isProposing: false,
			items: [],
			headAccounts: [],
			openCategories: [],
			errorMessage: ""
		};
	}

	async componentDidMount() {
		await this.load();
	}

	load = async () => {
		const { clientId, soldItems } = this.props;

		await this.update({ isLoading: true, errorMessage: "" });

		const result = await JaneReportService.listItemHeads({ clientId, soldItems });

		if (result.error) {
			await this.update({ isLoading: false, errorMessage: result.error });
			return;
		}

		await this.update({ isLoading: false, items: result.items, headAccounts: result.headAccounts || [] });
	};

	groupedByCategory = () => {
		const { items } = this.state;
		const groups = new Map();

		for (const item of items) {
			const category = item.incomeCategory || "Uncategorised";
			if (!groups.has(category)) {
				groups.set(category, { category, items: [], netAmount: 0, accountNames: new Set(), toDecide: 0 });
			}
			const group = groups.get(category);
			group.items.push(item);
			group.netAmount += item.netAmount;
			if (item.qboAccountName) {
				group.accountNames.add(item.qboAccountName);
			}
			if (!item.isConfirmed) {
				group.toDecide += 1;
			}
		}

		return [...groups.values()].sort((first, second) => second.netAmount - first.netAmount);
	};

	onProposeClicked = async () => {
		const { clientId, soldItems } = this.props;

		await this.update({ isProposing: true });
		const result = await JaneReportService.proposeItemHeads({ clientId, soldItems });
		await this.update({ isProposing: false });

		if (result.error) {
			UtilityService.alert({ type: "error", text: result.error });
			return;
		}

		await this.load();
	};

	confirm = async ({ decisions }) => {
		const { clientId, onChanged } = this.props;

		if (decisions.length === 0) {
			return;
		}

		await this.update({ isSaving: true });
		const result = await JaneReportService.confirmItemHeads({ clientId, decisions });
		await this.update({ isSaving: false });

		if (result.error) {
			UtilityService.alert({ type: "error", text: result.error });
			return;
		}

		await this.load();

		if (onChanged) {
			await onChanged();
		}
	};

	onItemAccountChosen = async ({ item, qboAccountId }) => {
		const { headAccounts } = this.state;
		const account = headAccounts.find(candidate => candidate.id === qboAccountId);

		await this.confirm({
			decisions: [
				{
					item: item.item,
					incomeCategory: item.incomeCategory,
					qboAccountId,
					qboAccountName: account ? account.name : null
				}
			]
		});
	};

	onCategoryConfirmed = async ({ group }) => {
		const decisions = group.items
			.filter(item => item.qboAccountId && !item.isConfirmed)
			.map(item => ({
				item: item.item,
				incomeCategory: item.incomeCategory,
				qboAccountId: item.qboAccountId,
				qboAccountName: item.qboAccountName
			}));

		await this.confirm({ decisions });
	};

	onAllProposalsConfirmed = async () => {
		const { items } = this.state;
		await this.onCategoryConfirmed({ group: { items } });
	};

	onCategoryToggled = async ({ category }) => {
		const { openCategories } = this.state;
		const isOpen = openCategories.includes(category);
		const next = isOpen ? openCategories.filter(name => name !== category) : [...openCategories, category];
		await this.update({ openCategories: next });
	};

	renderItemRow = ({ item }) => {
		const { headAccounts, isSaving } = this.state;

		const options = headAccounts.map(account => ({
			value: account.id,
			name: account.name,
			label: account.lineCount === 0 ? `${account.name} — never used here` : account.name
		}));

		const reasoningWhileUnconfirmed = item.reasoning && !item.isConfirmed ? item.reasoning : null;
		const correspondenceSourceText = describeCorrespondenceSource({ fromCorrespondence: item.fromCorrespondence });
		const correspondenceSourceLine = correspondenceSourceText ? <span className="jane-correspondence-source">{correspondenceSourceText}</span> : null;
		const sublabel = correspondenceSourceLine || reasoningWhileUnconfirmed;

		return (
			<ToolsMappingRow
				key={item.item}
				isChosen={item.isConfirmed}
				label={item.item}
				sublabel={sublabel}
				amount={UtilityService.formatMoney({ value: item.netAmount, emptyText: "—" })}
				options={options}
				value={item.qboAccountId}
				placeholder="Choose a revenue account…"
				searchPlaceholder="Search accounts…"
				disabled={isSaving}
				onChange={qboAccountId => this.onItemAccountChosen({ item, qboAccountId })}
			/>
		);
	};

	renderGroup = ({ group }) => {
		const { openCategories, isSaving } = this.state;

		const isOpen = openCategories.includes(group.category);
		const accountNames = [...group.accountNames];
		const isSingleAccount = accountNames.length === 1;
		const isSettled = group.toDecide === 0;
		const canConfirmWholeGroup = !isSettled && group.items.every(item => item.qboAccountId);

		return (
			<div className="item-heads__group" key={group.category}>
				<div className="item-heads__group-head">
					<button className="item-heads__group-toggle" onClick={() => this.onCategoryToggled({ category: group.category })} type="button">
						{isOpen ? <Icon.ChevronDown size={14} /> : <Icon.ChevronRight size={14} />}
						<span className="item-heads__group-name">{group.category}</span>
					</button>

					<span className="item-heads__group-meta tk-text-muted">
						{group.items.length} {group.items.length === 1 ? "item" : "items"} · {UtilityService.formatMoney({ value: group.netAmount, emptyText: "—" })}
					</span>

					<span className="item-heads__group-destination">
						{accountNames.length === 0 && <span className="tk-text-muted">nothing proposed yet</span>}
						{isSingleAccount && <span className="item-heads__group-account">{accountNames[0]}</span>}
						{accountNames.length > 1 && <span className="tk-text-muted">split across {accountNames.length} accounts</span>}
					</span>

					<span className="item-heads__group-action">
						{isSettled ? (
							<span className="tk-badge tk-badge--success">confirmed</span>
						) : (
							canConfirmWholeGroup && (
								<button className="tk-button tk-button--secondary tk-button--compact" onClick={() => this.onCategoryConfirmed({ group })} type="button" disabled={isSaving}>
									Confirm {group.toDecide}
								</button>
							)
						)}
					</span>
				</div>

				{isOpen && <div className="item-heads__items">{group.items.map(item => this.renderItemRow({ item }))}</div>}
			</div>
		);
	};

	render() {
		const { isLoading, isProposing, isSaving, items, errorMessage, headAccounts } = this.state;

		if (isLoading) {
			return (
				<div className="tk-panel tk-panel-loading">
					<Loader />
					<span className="tk-text-muted">Reading what this period sold…</span>
				</div>
			);
		}

		const confirmedCount = items.filter(item => item.isConfirmed).length;
		const proposedCount = items.filter(item => !item.isConfirmed && item.qboAccountId).length;
		const undecidedCount = items.length - confirmedCount;
		const hasNothingProposed = proposedCount === 0 && undecidedCount > 0;

		return (
			<ToolsCollapsiblePanel
				title="What each item is sold as"
				defaultOpen={undecidedCount > 0}
				status={<ToolsProgressBadge remainingCount={undecidedCount} totalCount={items.length} doneWord="confirmed" todoWord="confirm" />}
			>
				<div className="tk-lede">
					Every revenue line credits the account chosen for the item sold. Jane's own grouping is shown first because a whole group usually belongs
					in one account. Nothing a suggestion proposes is used until you confirm it.
				</div>

				{headAccounts.length === 0 && (
					<ToolsNotice
						tone="warning"
						icon={<Icon.AlertTriangle size={16} />}
						title="No revenue accounts could be read"
						detail="QuickBooks has to be connected for this client before items can be mapped."
						className="item-heads__notice"
					/>
				)}

				{undecidedCount > 0 && headAccounts.length > 0 && (
					<div className="tk-suggest-bar">
						<span className="tk-suggest-bar__text">
							{hasNothingProposed
								? `${undecidedCount} items have no account yet. A suggestion can be drafted for all of them, with its reasoning shown against each one.`
								: `${proposedCount} suggested, ${undecidedCount} still to confirm. Open a group to see the reasoning behind each suggestion.`}
						</span>
						{hasNothingProposed && (
							<button className="tk-button tk-button--secondary tk-button--compact" onClick={this.onProposeClicked} type="button" disabled={isProposing}>
								{isProposing ? "Drafting…" : "Draft suggestions"}
							</button>
						)}
						{proposedCount > 0 && (
							<button className="tk-button tk-button--secondary tk-button--compact" onClick={this.onAllProposalsConfirmed} type="button" disabled={isSaving}>
								Confirm all {proposedCount}
							</button>
						)}
					</div>
				)}

				<div>{this.groupedByCategory().map(group => this.renderGroup({ group }))}</div>

				{errorMessage && <div className="item-heads__error tk-text-muted">{errorMessage}</div>}
			</ToolsCollapsiblePanel>
		);
	}
}

export default ItemHeadsPanel;
